// client/src/hooks/useRoles.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const ROLES_QUERY_KEY = 'roles';

// ✅ Função para normalizar dados dos roles
const normalizeRoleData = (role) => {
    if (!role) return null;
    
    return {
        id: role._id || role.id,
        _id: role._id || role.id,
        name: role.name || '',
        description: role.description || '',
        scope: role.scope || '',
        account: role.account?._id || role.account,
        franquia: role.franquia?._id || role.franquia,
        permissions: role.permissions || [],
        isDefault: role.isDefault || false,
        createdAt: role.createdAt,
        updatedAt: role.updatedAt
    };
};

// --- HOOKS DE CONSULTA (QUERY) ---

/**
 * Hook para buscar uma lista de papéis (roles), opcionalmente filtrada.
 * @param {object} params - Parâmetros para a query (ex: { account: '...' })
 */
export const useRoles = (params = {}) => {
    return useQuery({
        queryKey: [ROLES_QUERY_KEY, params],
        queryFn: async () => {
            console.log('🔍 [useRoles] Buscando roles com params:', params);
            
            // Construir query params
            const queryParams = {};
            if (params.account) queryParams.account = params.account;
            if (params.franquia) queryParams.franquia = params.franquia;
            if (params.scope) queryParams.scope = params.scope;
            
            console.log('📤 [useRoles] Enviando requisição com params:', queryParams);
            
            try {
                const { data } = await apiClient.get('/roles', { params: queryParams });
                
                // Extrair array de roles da resposta
                let rolesArray = [];
                if (data.data && Array.isArray(data.data)) {
                    rolesArray = data.data;
                } else if (Array.isArray(data)) {
                    rolesArray = data;
                }
                
                // Normalizar cada item
                const normalizedRoles = rolesArray.map(normalizeRoleData);
                
                console.log('🔍 [useRoles] Roles recebidas:', normalizedRoles.length);
                console.log('📋 [useRoles] Scopes das roles:', normalizedRoles.map(r => ({ name: r.name, scope: r.scope })));
                
                return normalizedRoles;
                
            } catch (error) {
                console.error('❌ [useRoles] Erro ao buscar roles:', error);
                throw error;
            }
        },
        staleTime: 1000 * 60 * 5,
        retry: (failureCount, error) => {
            if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
                return false;
            }
            return failureCount < 2;
        }
    });
};

/**
 * ✅ Catálogo REAL de permissões existentes no sistema (config/permissions.js
 * no servidor) — achado ao mexer em nota fiscal: PermissoesPage.js antes
 * derivava a lista de checkboxes só das permissões já atribuídas a algum
 * Role existente (`roles.forEach(r => r.permissions...)`), então qualquer
 * permissão nova (nota_fiscal:*, matriz_nota_fiscal:*, fidelidade:*,
 * lista_espera:*, etc.) ficava invisível até alguém já ter um Role com ela
 * — impossível de acontecer pela própria UI (galo e ovo). Esta é a lista
 * completa de verdade, direto do backend.
 */
export const usePermissoesDisponiveis = () => {
    return useQuery({
        queryKey: [ROLES_QUERY_KEY, 'permissions-catalog'],
        queryFn: async () => {
            const { data } = await apiClient.get('/roles/permissions');
            return data.data || []; // array completo, achatado — ver rolesController.js::getAllPermissions
        },
        staleTime: 1000 * 60 * 15,
    });
};

/**
 * Hook para buscar um único papel pelo ID.
 */
export const useRoleById = (id) => {
    return useQuery({
        queryKey: [ROLES_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            const { data } = await apiClient.get(`/roles/${id}`);
            return normalizeRoleData(data.data);
        },
        enabled: !!id,
    });
};

// --- HOOKS DE AÇÃO (MUTATION) ---

/**
 * Hook que retorna as funções de mutação para Papéis.
 */
export const useMutateRoles = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = (roleId) => {
        queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
        if (roleId) {
            queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY, roleId] });
        }
    };

    const createRole = useMutation({
        mutationFn: (roleData) => apiClient.post('/roles', roleData),
        onSuccess: () => {
            invalidateCache();
            setAlert('Papel criado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar papel.', 'danger'),
    });

    const updateRole = useMutation({
        mutationFn: ({ id, roleData }) => apiClient.put(`/roles/${id}`, roleData),
        onSuccess: (data, variables) => {
            invalidateCache(variables.id);
            setAlert('Papel atualizado com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar papel.', 'danger'),
    });

    const deleteRole = useMutation({
        mutationFn: (id) => {
            if (window.confirm('Tem certeza que deseja apagar este papel?')) {
                return apiClient.delete(`/roles/${id}`);
            }
            return Promise.reject('Ação cancelada');
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Papel removido com sucesso.', 'success');
        },
        onError: (err) => {
            if (err !== 'Ação cancelada') {
                setAlert(err.response?.data?.error || 'Erro ao apagar papel.', 'danger');
            }
        },
    });

    return { createRole, updateRole, deleteRole };
};

/**
 * Hook principal para usar roles
 */
const useRole = () => {
    return {
        useRoles,
        useRoleById,
        useMutateRoles,
        normalizeRoleData
    };
};

export default useRole;